import React from "react";
import { Grid } from "@mui/material";
import {
  FacultyCard,
  UnitCardList,
  InstitutionCard,
  VenueCard,
} from "Explore/Components";
import { replaceFtsMatch } from "Explore/Utils/replaceFtsMatch";

const normalizeKeywords = (keywords) => {
  if (!keywords || !Array.isArray(keywords)) return [];
  return keywords.map((kw) => {
    if (typeof kw === "string") {
      return { value: kw };
    }
    if (typeof kw === "object" && kw !== null) {
      return {
        ...kw,
        value: kw.value || kw.name || kw.keyword || "",
      };
    }
    return { value: String(kw) };
  });
};

const normalizeResearcherItem = (item) => {
  if (!item) return {};
  let institution = item.institution;
  if (!institution && item.institution_name) {
    institution = {
      name: item.institution_name,
      id: item.institution_id || item.institution_name,
      explore_slug: item.institution_slug || item.institution_id,
    };
  } else if (typeof institution === "string") {
    institution = {
      name: institution,
      id: institution,
    };
  }
  return {
    ...item,
    institution,
    photo: item.photo || item.photo_url || item.avatar,
    photo_url: item.photo_url || item.photo || item.avatar,
    keywords: normalizeKeywords(item.keywords),
    publication_count:
      item.publication_count ?? item.total_publication ?? item.publications,
    citation_count:
      item.citation_count ?? item.total_citation ?? item.citations,
    h_index: item.h_index ?? item.total_h_index ?? item.hIndex,
    seniority: item.seniority ?? item.average_seniority,
  };
};

const normalizeUnitItem = (item) => {
  if (!item) return {};
  return {
    ...item,
    name: item.name || item.unit_name,
    id: item.id || item.unit_id,
    total_faculties: item.total_faculties ?? item.faculties,
    total_publication:
      item.total_publication ?? item.publication_count ?? item.publications,
    total_citation:
      item.total_citation ?? item.citation_count ?? item.citations,
    total_h_index: item.total_h_index ?? item.h_index ?? item.hIndex,
    keywords: normalizeKeywords(item.keywords),
  };
};

const normalizeInstitutionItem = (item) => {
  if (!item) return {};
  return {
    ...item,
    photo_url: item.photo_url || item.photo || item.logo_url,
    total_faculties: item.total_faculties ?? item.faculties,
    total_publication:
      item.total_publication ?? item.publication_count ?? item.publications,
    total_citation:
      item.total_citation ?? item.citation_count ?? item.citations,
    total_h_index: item.total_h_index ?? item.h_index ?? item.hIndex,
    keywords: normalizeKeywords(item.keywords),
  };
};

const normalizeVenueItem = (item) => {
  if (!item) return {};
  return {
    ...item,
    publication_count:
      item.publication_count ?? item.total_publication ?? item.publications,
    citation_count:
      item.citation_count ?? item.total_citation ?? item.citations,
    h_index: item.h_index ?? item.total_h_index ?? item.hIndex,
    keywords: normalizeKeywords(item.keywords),
  };
};

const cardContainerStyle = {
  background: "#ffffff",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
};

const CollectionListView = ({ items = [], entityType = "researchers" }) => {
  if (!items.length) {
    return (
      <div className="py-16 text-center text-gray-500 text-sm bg-white rounded-xl border border-dashed border-gray-200">
        No items in this collection.
      </div>
    );
  }

  const isUnit = entityType === "units" || entityType === "unit";
  const isInstitution =
    entityType === "institutions" || entityType === "institution";
  const isVenue = entityType === "venues" || entityType === "venue";

  return (
    <div className="w-full">
      <Grid container spacing={2.5}>
        {items.map((rawItem, index) => {
          const item = replaceFtsMatch(rawItem);
          return (
            <Grid item xs={12} key={item.id || index}>
              <div
                style={cardContainerStyle}
                className="hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                {isUnit ? (
                  <UnitCardList data={normalizeUnitItem(item)} hideActions />
                ) : isInstitution ? (
                  <InstitutionCard data={normalizeInstitutionItem(item)} hideActions />
                ) : isVenue ? (
                  <VenueCard data={normalizeVenueItem(item)} hideActions />
                ) : (
                  <FacultyCard data={normalizeResearcherItem(item)} hideActions />
                )}
              </div>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default CollectionListView;
